<template>
    <service-card
        class="accommodation-service"
        :service="formattedService"
        icon="mdi-hotel"
        :is-first="isFirst"
        :service-tips="serviceTips"
        :order="order"
        :variants-available="variantsAvailable"
        @show-info="showInfo"
        @modify-service="$emit('modify-service', $event)"
        @minimized-change="minimizedChange"
    >
        <template v-slot:checkbox>
            <slot name="checkbox" />
        </template>
        <template v-slot:icons>
            <slot name="icons" />
        </template>
        <template v-slot:orderInfo>
            <order-info-base
                :service="service"
                :service-check-in="serviceCheckIn"
                :service-check-out="serviceCheckOut"
                :is-package="isPackage"
                :service-image="mainImage"
                :order="order"
                :info="info"
                :order-end-date="orderEndDate"
                :not-itinerary-start="notItineraryStart"
                @cancel-service="$emit('cancel-service', $event)"
                @modify-service="$emit('modify-service', $event)"
            >
                <v-skeleton-loader v-if="isInfoLoading" type="paragraph" />
                <template v-if="info && info.address" v-slot:additionalInfo>
                    <hotel-address-label
                        :is-show-label="true"
                        :address="hotelAddress"
                        :hide-show-on-map-button="!info.latitude && !info.longitude"
                        @click="mapModal = true"
                    />
                </template>
                <div v-if="hotelConfirmationNumber" class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="small">mdi-check-underline</v-icon>
                        <span class="text-caption text-capitalize">{{ $t('hotel_confirmation') }} #</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ hotelConfirmationNumber }}
                    </span>
                </div>
                <div class="order-info-base-info">
                    <span class="order-info-base-info__icon">
                        <v-icon start size="x-small">mdi-weather-night</v-icon>
                        <span class="text-body-2 text-capitalize">{{ $t('night', nights) }}</span>
                    </span>
                    <span class="order-info-base-info__text">
                        {{ `${nights} ${$t('night', nights)}` }}
                    </span>
                </div>
                <div
                    v-if="service.status === 'Quote' && !isReadOnly"
                    class="accommodation-service__details d-flex align-center"
                >
                    <v-icon start size="small" color="primary" @click="fillAccommodationBookingDataModal = true">
                        mdi-pencil
                    </v-icon>
                    <span
                        class="text-caption cursor-pointer text-primary"
                        @click="fillAccommodationBookingDataModal = true"
                    >
                        {{ $t('enter_details') }}
                    </span>
                    <fill-accommodation-booking-data-modal
                        v-model="fillAccommodationBookingDataModal"
                        :service="service"
                        :order="order"
                    />
                </div>
                <template v-slot:travelers="{emptyAdditionalFields}">
                    <v-row v-for="room in rooms" :key="room.roomId" no-gutters>
                        <v-col class="accommodation-service__travelers-col mt-3">
                            <div class="accommodation-service__travelers-title d-flex text-subtitle-1">
                                <v-progress-circular
                                    v-if="isInfoLoading"
                                    indeterminate
                                    size="24"
                                    class="me-2"
                                    color="grey"
                                />
                                <v-icon v-else start size="small" @click="roomInfo(room)">
                                    mdi-information-outline
                                </v-icon>
                                <div class="accommodation-service__travelers-title-main d-flex align-center">
                                    <v-icon start size="x-small">mdi-bed-empty</v-icon>
                                    <p
                                        class="accommodation-service__travelers-title-txt text-subtitle-2 font-weight-bold"
                                    >
                                        {{ room.roomTypeName }}
                                    </p>
                                    <span
                                        v-if="
                                            service.serviceDetails[0].ownAccommodationInfo &&
                                            service.serviceDetails[0].ownAccommodationInfo.tariffName
                                        "
                                        class="text-grey-lighten-1 text-subtitle-2 font-weight-regular"
                                    >
                                        &nbsp;({{ service.serviceDetails[0].ownAccommodationInfo.tariffName }})
                                    </span>
                                </div>
                                <hotel-bed-type-label
                                    v-if="room.bedTypes && room.bedTypes.length"
                                    :bed-types="room.bedTypes"
                                    class="ms-3"
                                />
                            </div>
                            <!-- Display amenities for Zero sustainability level rooms -->
                            <div v-if="hasRoomAmenities(room)" class="mt-3 mb-3">
                                <v-divider class="mb-3" />
                                <div class="text-subtitle-2 font-weight-bold mb-2">
                                    <v-icon start size="small">mdi-leaf</v-icon>
                                    {{ $t('zero_room_amenities.selected_amenities') }}
                                </div>
                                <div class="amenities-list">
                                    <v-chip
                                        v-for="amenity in getRoomAmenities(room)"
                                        :key="amenity.id"
                                        size="small"
                                        class="ma-1"
                                        color="success"
                                        variant="outlined"
                                    >
                                        <v-icon start size="x-small">mdi-check-circle</v-icon>
                                        {{ amenity.name }}
                                        <span v-if="amenity.count && amenity.count > 1" class="ml-1">
                                            ({{ amenity.count }})
                                        </span>
                                    </v-chip>
                                </div>
                            </div>
                            <v-divider class="my-3" />
                            <travelers
                                :service="service"
                                :travelers="roomTravelers(room)"
                                :product="service.serviceType"
                                :details="service.serviceDetails"
                                :order-end-date="orderEndDate"
                                :empty-additional-fields="emptyAdditionalFields"
                            >
                                <template v-slot:mealType="{traveler}">
                                    <v-col cols="12" md="2">
                                        <small class="d-block traveler-label text-body-2">
                                            {{ $t('filterTitles.mealTypes') }}
                                        </small>
                                        <div class="d-flex align-center">
                                            <v-progress-circular
                                                v-if="isInfoLoading"
                                                indeterminate
                                                size="24"
                                                class="me-2"
                                                color="grey"
                                            />
                                            <v-icon
                                                v-else
                                                start
                                                :loading="isInfoLoading"
                                                size="small"
                                                @click="mealTypeInfo(room)"
                                            >
                                                {{
                                                    room.otaMealTypeCode !== '14'
                                                        ? ' mdi-silverware-fork-knife'
                                                        : 'mdi-information-outline'
                                                }}
                                            </v-icon>
                                            <span class="text-uppercase text-black font-weight-bold">
                                                {{ traveler.mealType ? traveler.mealType : room.mealTypeName }}
                                            </span>
                                        </div>
                                    </v-col>
                                </template>
                            </travelers>
                        </v-col>
                    </v-row>
                    <ModalWrapper v-if="roomData" v-model="roomModal" width="600" :scrim="false">
                        <template v-slot:content>
                            <div class="mb-2 text-h3">
                                {{ service.serviceName }}
                            </div>
                            <div v-if="roomData.roomTypeName" class="mb-1">
                                <span class="font-weight-medium">{{ $t('roomGroupBy.name') }}</span>
                                : {{ roomData.roomTypeName }}
                            </div>
                            <div v-if="roomData.mealTypeName" class="mb-1">
                                <span class="font-weight-medium">{{ $t('roomGroupBy.name') }}</span>
                                : {{ roomData.mealTypeName }}
                            </div>
                        </template>
                    </ModalWrapper>
                </template>
            </order-info-base>
        </template>
        <template v-slot:serviceInfo>
            <service-info-dialog
                ref="serviceInfoDialog"
                v-model="modal"
                :service="formattedService"
                :info="infoData"
                :image="mainImage"
                :is-info-loading="isInfoLoading"
                enable-custom-product-info
                @close-modal="closeModal"
            >
                <v-icon class="v-card-close" @click="modal = false">mdi-close</v-icon>
                <template v-if="info" v-slot:serviceInfo>
                    <div class="d-flex align-center">
                        <v-icon start size="small" color="info">mdi-map-marker</v-icon>
                        <small>{{ hotelAddress }}</small>
                    </div>
                    <div
                        v-if="info.email && showContacts && isShowHotelEmailOnInfo"
                        class="d-flex align-center text-caption"
                    >
                        <v-icon start size="small">mdi-at</v-icon>
                        {{ info.email }}
                    </div>
                    <div v-if="info.url" class="d-flex align-center text-caption">
                        <v-icon start size="small">mdi-web</v-icon>
                        <a :href="info.url" target="_blank" class="text-caption">
                            {{ info.url }}
                        </a>
                    </div>
                    <v-row no-gutters>
                        <v-col v-if="info.phone && showContacts" :cols="12" md="12">
                            <div class="d-flex align-center text-caption">
                                <v-icon start size="small">mdi-phone</v-icon>
                                {{ info.phone }}
                            </div>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col v-if="info.fax && showContacts" :cols="12" md="12">
                            <div class="d-flex align-center text-caption">
                                <v-icon start size="small">mdi-fax</v-icon>
                                {{ info.fax }}
                            </div>
                        </v-col>
                    </v-row>
                </template>
                <!-- TODO: rewrite descriptions like in HotelPage (hotel-info-descriptions) -->
                <template v-if="info" v-slot:extraInfo>
                    <v-col :cols="12" md="12" class="mt-5">
                        <div v-if="info.descriptions.description.find(el => el.type === 'short')" class="mt-2">
                            <span class="d-block text-subtitle-1 font-weight-medium">
                                {{ $t('description_type.short') }}
                            </span>
                            <span v-html="info.descriptions.description.find(el => el.type === 'short').description" />
                        </div>
                        <div v-if="info.descriptions.description.find(el => el.type === 'general')" class="mt-2">
                            <span class="d-block text-subtitle-1 font-weight-medium">
                                {{ $t('description_type.general') }}
                            </span>
                            <span
                                v-html="info.descriptions.description.find(el => el.type === 'general').description"
                            />
                        </div>
                        <div v-if="info.descriptions.description.find(el => el.type === 'pleasenote')" class="mt-2">
                            <span class="d-block text-subtitle-1 font-weight-medium">
                                {{ $t('important_information') }}
                            </span>
                            <span
                                v-html="info.descriptions.description.find(el => el.type === 'pleasenote').description"
                            />
                        </div>
                    </v-col>
                    <v-col :cols="12" md="8" class="mt-3">
                        <div class="hidden-lg-and-down">
                            <v-row no-gutters>
                                <v-col class="col-title">
                                    <v-icon start size="small">mdi-star-circle-outline</v-icon>
                                    {{ $t('services') }}:
                                </v-col>
                                <v-col class="pl-2 d-flex flex-wrap">
                                    <v-chip
                                        v-for="service in info.services.service"
                                        :key="service.supplierSpecificCode"
                                        size="small"
                                        class="px-2 mr-1 mt-1"
                                    >
                                        {{ service.otaname || service.supplierServiceName }}
                                    </v-chip>
                                </v-col>
                            </v-row>
                        </div>
                    </v-col>
                    <hotel-amenities :info="info" />
                </template>
            </service-info-dialog>
            <client-only>
                <v-dialog v-if="info && info.latitude && info.longitude" v-model="mapModal" :max-width="500">
                    <map-location
                        v-if="mapModal"
                        :latitude="info.latitude"
                        :longitude="info.longitude"
                        :height="`500px`"
                    />
                </v-dialog>
            </client-only>
        </template>
    </service-card>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Inject, Setup} from 'vue-facing-decorator'
    import ServiceInfoDialog from '~/components/account/orderServices/ServiceInfoDialog.vue'
    import ServiceCard from '~src/components/account/orderServices/serviceCard.src'
    import Travelers from '~src/components/account/orderServices/travelers.src'
    import MapLocation from '~src/components/info/mapLocation.src'
    import OrderInfoBase from '~src/components/account/orderServices/orderInfoBase.src'
    import HotelBedTypeLabel from '@/components/hotels/snippets/HotelBedTypeLabel.vue'
    import {ServiceBase} from '@/components/account/orderServices/ServiceBase.vue'
    import FillAccommodationBookingDataModal from '@/components/account/modals/newOrderRequest/FillAccommodationBookingDataModal.vue'
    import HotelAddressLabel from '~src/components/hotels/snippets/hotelAddressLabel.src'
    import {hotelAddressToString} from '~/utils/helpers'
    import HotelAmenities from '~src/components/info/HotelAmenities.vue'
    import HotelStars from '~/components/hotels/snippets/HotelStars.vue'
    import {EventBus, SHOW_MEAL_TYPE_INFO_EVENT, SHOW_ROOM_INFO_MODAL_EVENT} from '~/utils/event-bus'

    @Component({
        components: {
            HotelStars,
            HotelAddressLabel,
            FillAccommodationBookingDataModal,
            MapLocation,
            Travelers,
            ServiceCard,
            OrderInfoBase,
            ServiceInfoDialog,
            HotelBedTypeLabel,
            HotelAmenities,
        },
    })
    export class AccommodationService extends ServiceBase {
        @Setup(() => useCustomizationHelpers().isShowHotelEmailOnInfo) isShowHotelEmailOnInfo

        @Prop({required: true}) service
        @Prop() itineraryService
        @Prop({default: false}) isPackage
        @Prop() isFirst
        @Inject({default: false}) isReadOnly

        info = null
        moreInfo = false
        hasImages = true
        modal = false
        mapModal = false
        roomModal = false
        roomData = null
        fillAccommodationBookingDataModal = false

        @Emit()
        addMarker(latitude, longitude) {
            return {lat: latitude, lng: longitude, type: 'hotel'}
        }

        @Emit()
        infoLoaded(info) {
            return info
        }

        closeModal() {
            this.modal = false
        }

        async loadProductInfo() {
            this.isInfoLoading = true

            const {hotelId, cityCode, hotelCode, hotelCityCode} = this.service.serviceDetails[0]
            const rq = hotelId
                ? {hotelId}
                : {cityCode: hotelCityCode || cityCode, hotelCode, supplierCode: this.service.supplierCode}
            try {
                const response = await this.$api.hotelInfo.get(rq)
                this.info = response.hotelDescriptionInfo
                if (this.info.latitude && this.info.longitude) {
                    this.addMarker(this.info.latitude, this.info.longitude)
                }
                this.infoLoaded(this.info)

                //eslint-disable-next-line no-empty
            } catch (e) {}

            this.isInfoLoading = false
            this.infoLoadingFinished = true
        }

        showRoomModal(data) {
            this.roomModal = true
            this.roomData = data
        }

        getTravelerName(traveler) {
            return `${traveler.prefix} ${traveler.name[0].firstName} ${traveler.name[0].lastName}`
        }

        roomTravelers(room) {
            return this.service.travelers.filter(({travelerId}) => room.travelerIds.travelerId.includes(travelerId))
        }

        async roomInfo(room) {
            let roomInfo
            if (this.isOwnProduct) {
                roomInfo = this.info?.roomTypes.roomType.find(roomType => roomType.id === room.roomTypeId)
            } else {
                roomInfo = this.info?.roomTypes.roomType.find(
                    roomType => roomType.code === room.code || roomType.code === room.roomCode
                )
            }
            if (roomInfo) {
                EventBus.$emit(SHOW_ROOM_INFO_MODAL_EVENT, {
                    roomInfo: roomInfo,
                })
            } else {
                this.$toast.error(this.$t('error_message.load_error'))
            }
        }

        async mealTypeInfo(room) {
            EventBus.$emit(SHOW_MEAL_TYPE_INFO_EVENT, {
                hotelInfo: this.info,
                mealTypeNames: [room.mealTypeName],
            })
        }

        get isOwnProduct() {
            return runtimeStore.ownProduct(this.service.supplierCode)
        }

        get showContacts() {
            return true
        }

        get formattedService() {
            // TODO: temporary solution(block by core), GPFE-4133
            if (this.info?.rating) {
                const serviceDetails = JSON.parse(JSON.stringify(this.service.serviceDetails))
                serviceDetails[0].ratings = [
                    {
                        ratingSource: 'INTERNAL',
                        rating: this.info.rating,
                    },
                ]

                return {
                    ...this.service,
                    serviceDetails,
                }
            }
            return this.service
        }

        get hotelAddress() {
            return hotelAddressToString(this.info?.address, this.service.serviceDetails[0])
        }

        get rooms() {
            return this.service.serviceDetails[0].rooms
        }

        get serviceCheckIn() {
            const time = this.service.serviceDetails[0].checkInFrom
            const date = this.$filters.dateFormat(this.service.startDateTime)
            return time ? `<span>${date}</span> <small>${this.$t('time_from')} ${time}</small>` : date
        }

        get serviceCheckOut() {
            const time = this.service.serviceDetails[0].checkOutTo
            const date = this.$filters.dateFormat(this.service.endDateTime)
            return time ? `<span>${date}</span> <small>${this.$t('time_before')} ${time}</small>` : date
        }

        get mainImage() {
            return this.info && this.info.mainImage ? this.info.mainImage.url : ''
        }

        get nights() {
            const startDate = new Date(this.service.startDateTime.slice(0, 10))
            const endDate = new Date(this.service.endDateTime.slice(0, 10))
            return (new Date(endDate) - new Date(startDate)) / 8.64e7
        }

        get infoData() {
            return {...this.info, ...{mainImage: this.mainImage}}
        }

        get roomTypes() {
            //TODO roomCategory !== name need discuss
            /*return this.info.roomTypes.roomType.reduce((roomTypes, roomType) => {
                const nameKey = roomType.roomCategory ? 'roomCategory' : 'name'
                if (this.$t('description_type.other') === roomType[nameKey]) {
                    return roomTypes
                }
                if (roomTypes.findIndex(rt => roomType[nameKey] === rt[nameKey]) === -1) {
                    roomTypes.push(roomType)
                }
                return roomTypes
            }, [])*/
            return this.info.roomTypes.roomType
        }

        get notItineraryStart() {
            return this.itineraryService?.type?.start === false
        }

        get isTO1() {
            return authStore.isTO1
        }

        get hotelConfirmationNumber() {
            return this.service.serviceDetails[0].hotelConfirmationNumber
        }

        /**
         * Get amenities for a specific room based on its sustainability level
         * For Zero rooms, return the user-selected amenities from service.roomAmenities
         */
        getRoomAmenities(room: any) {
            // Only show amenities for ZERO sustainability level rooms
            if (room.sustainabilityLevel !== 'ZERO') {
                return []
            }

            // Return the roomAmenities from the service
            return this.service.roomAmenities || []
        }

        /**
         * Check if a room has amenities to display
         */
        hasRoomAmenities(room: any) {
            return this.getRoomAmenities(room).length > 0
        }
    }

    export default AccommodationService
</script>

<style lang="scss" scoped>
    .thumb {
        max-width: 350px;
    }

    .col-title {
        white-space: nowrap;
        max-width: fit-content;
    }

    .info-text {
        color: black;
        font-size: 1rem;
        font-style: italic;
    }

    .payment-status {
        width: 100%;
        text-align: center;
        display: flex;
        justify-content: center;
        min-width: fit-content;
        max-width: fit-content;
    }

    :deep(.v-dialog) {
        background: #fff;
        padding: 20px;
    }

    .hotel-address {
        line-height: 1;
    }

    .modal-close-btn-block {
        position: relative;
        top: 12px;
        right: 12px;
    }

    .amenities-list {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    }
</style>
